/**
 * CP-55 shopper listing freshness. CP-76 adds an age stamp for stale cards.
 *
 * The server decides freshness from the same max PriceHistory.timestamp the
 * lead-in fare uses (≤48h). The UI shows that fare only together with the
 * server's "Updated … UTC" cue. When price_fresh is false the dollar stays
 * hidden. A recorded fare (positive current_price) is "Price not current",
 * not "Price unavailable" — the API still has that price. No recorded fare
 * stays "Price unavailable". If price_updated_at is present the card still
 * shows "Last observed … UTC" so a stale fare is not labeled as freshly Updated.
 */

export const PRICE_NOT_CURRENT = 'Price not current';
export const PRICE_UNAVAILABLE = 'Price unavailable';

const OBSERVED_MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

export function showsListingPrice(record) {
  if (!record || record.price_fresh !== true) return false;
  const label = record.price_updated_label;
  if (typeof label !== 'string' || !label.trim()) return false;
  const n = Number(record.current_price);
  return Number.isFinite(n) && n > 0;
}

export function listingPriceCue(record) {
  if (!showsListingPrice(record)) return '';
  return record.price_updated_label;
}

/**
 * CP-79: cabin label next to a shown From/$ fare.
 *
 * Returns the lead-in cabin (current_cabin_type / cabin_type) only when the
 * card already shows a listing price. Missing, blank, or "Unknown" stay
 * empty — never invent a cabin on Price unavailable / not current cards.
 */
export function listingCabinLabel(record) {
  if (!showsListingPrice(record)) return '';
  const raw = record.current_cabin_type || record.cabin_type;
  if (raw == null) return '';
  const text = String(raw).trim();
  if (!text || text.toLowerCase() === 'unknown') return '';
  return text;
}

/**
 * CP-119: one section-level cue for a card grid instead of the same
 * "Updated … UTC" line repeated on every card.
 *
 * Returns the server label only when every card that shows a fare carries
 * the exact same label. Mixed labels (or no priced cards) return '' so each
 * card keeps its own cue — a fare is never shown without its own Updated
 * stamp being on screen. Stale / hidden fares are ignored here; they keep
 * their per-card "Last observed … UTC" age stamp.
 */
export function sharedListingPriceCue(records) {
  if (!Array.isArray(records)) return '';
  let shared = '';
  for (const record of records) {
    if (!showsListingPrice(record)) continue;
    const label = record.price_updated_label;
    if (!shared) {
      shared = label;
    } else if (label !== shared) {
      return '';
    }
  }
  return shared;
}

/** True when the payload still carries a positive fare we are not quoting. */
export function hasRecordedPrice(record) {
  if (!record) return false;
  const n = Number(record.current_price);
  return Number.isFinite(n) && n > 0;
}

/**
 * Copy for a hidden dollar. Stale (price on the payload, not fresh) is
 * "Price not current". No fare at all is "Price unavailable".
 */
export function hiddenPriceReason(record) {
  if (showsListingPrice(record)) return '';
  if (hasRecordedPrice(record)) return PRICE_NOT_CURRENT;
  return PRICE_UNAVAILABLE;
}

/**
 * Shopper age line for a fare we will not quote as current.
 * Formats price_updated_at in UTC. Never returns the fresh "Updated …" cue.
 */
export function formatObservedAgeStamp(value) {
  if (typeof value !== 'string') return '';
  const text = value.trim();
  if (!/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(text)) return '';
  let iso = text;
  if (!/(?:Z|[+-]\d{2}:?\d{2})$/i.test(iso)) iso += 'Z';
  const parsed = Date.parse(iso);
  if (Number.isNaN(parsed)) return '';
  const stamp = new Date(parsed);
  const hour24 = stamp.getUTCHours();
  const hour = hour24 % 12 || 12;
  const ampm = hour24 < 12 ? 'AM' : 'PM';
  const minute = String(stamp.getUTCMinutes()).padStart(2, '0');
  const month = OBSERVED_MONTHS[stamp.getUTCMonth()];
  return (
    `Last observed ${month} ${stamp.getUTCDate()}, ${stamp.getUTCFullYear()}, ` +
    `${hour}:${minute} ${ampm} UTC`
  );
}

export function staleAgeStamp(record) {
  if (!record || showsListingPrice(record)) return '';
  return formatObservedAgeStamp(record.price_updated_at);
}

/**
 * A price-drop claim needs a positive fare strictly below a non-null peak.
 * Flat (current === peak) and rising (current at the high, or a nulled
 * baseline) are not drops. Accepts either detail (`price_drop_amount`) or
 * card (`price_drop`) field names.
 */
export function priceDropMathIsHonest(details) {
  if (!details) return false;
  const peak = Number(details.peak_price);
  const drop = Number(
    details.price_drop_amount != null ? details.price_drop_amount : details.price_drop
  );
  const current = Number(details.current_price);
  if (!Number.isFinite(peak) || !Number.isFinite(drop) || !Number.isFinite(current)) {
    return false;
  }
  return peak > 0 && drop > 0 && current > 0 && current < peak;
}

/**
 * Detail HOT DEAL: displayable fare and an honest price_drop.
 * CP-87: a shown cabin the server marked rising is not a hot deal, even
 * when an older peak still clears the drop arithmetic.
 */
export function showsHotDealBadge(record) {
  if (!showsListingPrice(record)) return false;
  const status = record.deal_status;
  if (!status || status.is_hot_deal !== true) return false;
  if (status.shown_cabin_rising === true) return false;
  if (status.hot_deal_type !== 'price_drop') return false;
  return priceDropMathIsHonest(status.hot_deal_details);
}

/**
 * Detail / Best Deals BEST DEAL: displayable fare the server accepted.
 * CP-87: table membership is not enough. A rising shown cabin, a
 * zero-variance shown cabin (Current == Lowest == Highest, even when
 * observation_count is 14), or fewer than two observations, does not
 * badge even if a stale flag says so. HOT does not use this variance gate.
 */
export function showsBestDealBadge(record) {
  if (!showsListingPrice(record)) return false;
  const status = record.deal_status;
  if (!status || status.is_best_deal !== true) return false;
  if (status.shown_cabin_rising === true) return false;
  if (status.zero_variance === true) return false;
  if (typeof status.observation_count === 'number' && status.observation_count < 2) {
    return false;
  }
  return true;
}

/** Hot-deals card PRICE DROP chip: displayable fare and a real drop. */
export function showsPriceDropBadge(deal) {
  if (!showsListingPrice(deal)) return false;
  return priceDropMathIsHonest(deal);
}
